import BeakSvg from '@/assets/icons/beak.svg?react'
import CartSvg from '@/assets/icons/cart.svg?react'
import useAccountInformation from '@/auth/hooks/useAccountInformation'
import useCurrentVertical, {Vertical} from '@/hooks/useCurrentVertical'
import {useCart} from '@/lib/cart/hooks/useCart'
import {getFoodGrubhubCartQueryOptions} from '@/lib/food-grubhub/hooks/useFoodGrubhubCart'
import useGrubhubGetAuth from '@/lib/food-grubhub/hooks/useGrubhubGetAuth'
import {useGetGrubhubUserAddress} from '@/lib/food-grubhub/store'
import {useLocale} from '@/lib/locale/locale'
import {CartRoute, FoodGrubhubCartRoute} from '@/routeRegistry'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import style from './CartLink.module.scss'

interface Props {
  className?: string
  activeClassName?: string
}

export default memo<Props>(function CartLink({className, activeClassName}) {
  const [locale] = useLocale()
  const [account] = useAccountInformation()
  const currentVertical = useCurrentVertical(Vertical.Shop)

  const {data: shopCart} = useCart(locale, false)

  const store = useStore()
  const getUserAddress = useGetGrubhubUserAddress(store)

  const {data: authResponse} = useGrubhubGetAuth()
  const accessToken = authResponse?.session_handle.access_token
  const latestAccessToken = useLatest(accessToken)
  const getAccessToken = () => latestAccessToken.current

  const {data: foodGrubhubCart} = useQuery({
    ...getFoodGrubhubCartQueryOptions(locale, account, getUserAddress, getAccessToken),
    enabled: currentVertical === Vertical.FoodGrubHub,
  })

  const count = (() => {
    switch (currentVertical) {
      case Vertical.Shop:
        return shopCart?.itemsLength ?? 0
      case Vertical.FoodGrubHub:
        return foodGrubhubCart?.foodCartItems.reduce((acc, item) => acc + item.quantity, 0) ?? 0
      case Vertical.Flight:
      case undefined:
        return 0
    }
  })()

  const location = useLocation()

  const isOnCart =
    location.pathname ===
    (currentVertical === Vertical.Shop ? CartRoute.fullPath : FoodGrubhubCartRoute.fullPath)

  const to = (() => {
    switch (currentVertical) {
      case Vertical.Shop:
        return CartRoute.fullPath
      case Vertical.FoodGrubHub:
        return FoodGrubhubCartRoute.fullPath
      case Vertical.Flight:
      case undefined:
        return CartRoute.fullPath
    }
  })()

  return (
    <div className={clsx(style.CartLink, className)}>
      <Link
        to={to}
        activeProps={{
          'className': activeClassName,
          'aria-current': 'page',
        }}
      >
        {count > 0 && (
          <div className={style.ItemNum} aria-hidden>
            {count}
          </div>
        )}
        <CartSvg aria-hidden />
        <VisuallyHidden>{`Cart (${count} items)`}</VisuallyHidden>
      </Link>
      {!isOnCart && count > 0 && (
        <>
          <BeakSvg className={style.Beak} aria-hidden />
          <div className={style.UserDropdown}>
            <div aria-hidden>
              {count} item{count > 1 && 's'} in cart
            </div>
            <div aria-hidden>
              <Link to={to}>Check out</Link>
            </div>
          </div>
        </>
      )}
    </div>
  )
})
